/**
 * Number and currency formatting utilities
 */

import type { BigDecimal } from "@solomon-labs/bigdecimal";

/**
 * Options for formatting numbers
 */
export interface FormatNumberOptions {
    /** Minimum decimal places (default 0) */
    minimumFractionDigits?: number;
    /** Maximum decimal places (default 2) */
    maximumFractionDigits?: number;
    /** Floor-truncate to maximumFractionDigits instead of rounding. Use for balance displays to never overstate. */
    truncate?: boolean;
}

/**
 * Format a number with commas and decimal places
 *
 * @param value - Number or string to format
 * @param options - Formatting options
 * @returns Formatted number string
 *
 * @example
 * formatNumber(1234567.89) // "1,234,567.89"
 * formatNumber("1000", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) // "1,000.00"
 * formatNumber(1234.5678, { maximumFractionDigits: 4 }) // "1,234.5678"
 */
export function formatNumber(value: number | string, options: FormatNumberOptions = {}): string {
    const num = typeof value === "string" ? parseFloat(value) : value;

    if (isNaN(num)) {
        return "0";
    }

    const { minimumFractionDigits = 0, maximumFractionDigits = 2, truncate = false } = options;

    if (truncate) {
        const isNeg = num < 0;
        const absStr = Math.abs(num).toString();
        const [intPart, decPart = ""] = absStr.split(".");
        const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        const prefix = isNeg ? "-" : "";

        if (maximumFractionDigits === 0) {
            return `${prefix}${formattedInt}`;
        }

        let truncatedDec = decPart.slice(0, maximumFractionDigits);
        truncatedDec = truncatedDec.padEnd(minimumFractionDigits, "0");

        if (truncatedDec) {
            return `${prefix}${formattedInt}.${truncatedDec}`;
        }

        return `${prefix}${formattedInt}`;
    }

    return num.toLocaleString("en-US", {
        minimumFractionDigits,
        maximumFractionDigits
    });
}

/**
 * Format a value as USD currency
 *
 * @param value - Number or string to format
 * @param minDecimals - Minimum decimal places (default 2)
 * @param maxDecimals - Maximum decimal places (default 2)
 * @returns Formatted currency string with $ prefix
 *
 * @example
 * formatCurrency(1234.56) // "$1,234.56"
 * formatCurrency("1000") // "$1,000.00"
 */
export function formatCurrency(value: number | string, minDecimals: number = 2, maxDecimals: number = 2): string {
    const num = typeof value === "string" ? parseFloat(value) : value;

    if (isNaN(num)) {
        return "$0.00";
    }

    return num.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: minDecimals,
        maximumFractionDigits: maxDecimals
    });
}

/**
 * Format a large currency value in compact notation (k, M, B)
 *
 * @param value - Number or string to format
 * @param decimals - Decimal places to show (default 1)
 * @returns Formatted compact currency string
 *
 * @example
 * formatCurrencyCompact(1500000) // "$1.5M"
 * formatCurrencyCompact("250000", 2) // "$250.00k"
 */
export function formatCurrencyCompact(value: number | string, decimals: number = 1): string {
    const num = typeof value === "string" ? parseFloat(value) : value;

    if (isNaN(num)) {
        return "$0";
    }

    const absNum = Math.abs(num);

    if (absNum >= 1_000_000_000) {
        return `$${(num / 1_000_000_000).toFixed(decimals)}B`;
    }

    if (absNum >= 1_000_000) {
        return `$${(num / 1_000_000).toFixed(decimals)}M`;
    }

    if (absNum >= 1_000) {
        return `$${(num / 1_000).toFixed(decimals)}k`;
    }

    return formatCurrency(num, 0, decimals);
}

/**
 * Format a percentage value
 *
 * @param value - Percentage value (15.2 means 15.2%)
 * @param decimals - Decimal places to show (default 1)
 * @returns Formatted percentage string
 *
 * @example
 * formatPercent(15.23) // "15.2%"
 * formatPercent(5, 2) // "5.00%"
 */
export function formatPercent(value: number | string, decimals: number = 1): string {
    const num = typeof value === "string" ? parseFloat(value) : value;

    if (isNaN(num)) {
        return "0%";
    }

    // Value is already a percentage (e.g., 15.2 means 15.2%)
    return `${num.toFixed(decimals)}%`;
}

/**
 * Truncate an address for display
 *
 * @param address - Full address string
 * @param startChars - Number of characters to show at start (default 4)
 * @param endChars - Number of characters to show at end (default 4)
 * @returns Truncated address
 *
 * @example
 * truncateAddress("7xKXtg2CW87d97TXJSDpbD5jBkheTqA83TZRuJosgAsU")
 * // "7xKX...AsU"
 */
export function truncateAddress(address: string, startChars: number = 4, endChars: number = 4): string {
    if (address.length <= startChars + endChars) {
        return address;
    }
    return `${address.slice(0, startChars)}...${address.slice(-endChars)}`;
}

/**
 * Formats a raw token amount (BigDecimal) to user viewable format (takes decimals into account)
 *
 * @param num - BigDecimal amount in raw token units
 * @param decimals - Token decimals
 * @param withCommas - Add thousand separators (default true)
 * @param minDecimals - Minimum decimal places (default 2)
 * @param maxDecimals - Maximum decimal places (default 4)
 * @returns Formatted token amount string
 *
 * @example
 * formatTokenNumber(new BigDecimal("1234567890"), 6, true, 2, 4) // "1,234.5679"
 */
export function formatTokenNumber(
    num: BigDecimal,
    decimals: number,
    withCommas: boolean = true,
    minDecimals: number = 2,
    maxDecimals: number = 4
): string {
    // Convert the BigDecimal to a string (get the raw bigint value)
    const bnString = num.toBigint().toString();

    // Pad the string with leading zeros if it's too short
    const paddedString = bnString.padStart(decimals + 1, "0");

    // Insert the decimal point at the correct position
    let integerPart = paddedString.slice(0, -decimals);
    let decimalPart = paddedString.slice(-decimals);

    if (decimalPart.length > maxDecimals) {
        decimalPart = decimalPart.slice(0, maxDecimals);
    } else if (decimalPart.length < minDecimals) {
        decimalPart = decimalPart.padEnd(minDecimals, "0");
    }

    if (withCommas) {
        // Add commas to the integer part
        integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

        // Combine the formatted integer part with the decimal part
        return `${integerPart}.${decimalPart}`;
    }

    return `${integerPart}.${decimalPart}`;
}

/**
 * Safe toNumber conversion for BigDecimal values.
 * Handles BigDecimal instances, plain numbers, and unknown types gracefully.
 */
export function safeToNumber(value: unknown): number {
    if (value && typeof (value as { toNumber?: () => number }).toNumber === "function") {
        return (value as { toNumber: () => number }).toNumber();
    }
    if (typeof value === "number") return value;
    return 0;
}

/**
 * Dynamically show different significant digits based on the amount
 *
 * @param amount - Numeric amount to format
 * @returns Formatted string with appropriate significant digits
 *
 * @example
 * formatNumberSignificantDigits(1234567) // "1234567"
 * formatNumberSignificantDigits(1234.56) // "1234.56"
 * formatNumberSignificantDigits(12.345678) // "12.3457"
 * formatNumberSignificantDigits(1.23456789) // "1.234568"
 * formatNumberSignificantDigits(0.123456) // "0.123456"
 */
export function formatNumberSignificantDigits(amount: number): string {
    if (amount === 0) return "0";

    let formatted: string;

    if (amount >= 1000000) {
        formatted = amount.toFixed(0);
    } else if (amount >= 1000) {
        formatted = amount.toFixed(2);
    } else if (amount >= 10) {
        formatted = amount.toFixed(4);
    } else if (amount >= 1) {
        formatted = amount.toFixed(6);
    } else {
        // < 1: show all decimals without rounding
        formatted = amount.toString();
    }

    return formatted.replace(/\.?0+$/, "");
}
